import {
  ArrowRight,
  Building2,
  Calendar,
  Car,
  CheckCircle,
  Clock,
  Coffee,
  CreditCard,
  Headphones,
  Loader,
  Monitor,
  Phone,
  Printer,
  Settings,
  Shield,
  Star,
  Users,
  Wifi,
  Zap
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import FlexOfficeFooter from '../components/FlexOfficeFooter';
import FlexOfficeHeader from '../components/FlexOfficeHeader';

const FlexFeatures = () => {
  const [isLoading, setIsLoading] = useState(true);
  const company = useSelector((state) => state?.companyReducer?.company) || {};
  const operatingLocationCode = company?.operatingLocation?.code || "US";

  const getCountryCode = (contactCode) => {
    if (contactCode) {
      return contactCode;
    }

    return operatingLocationCode === "IND" ? "+91" : "+1";
  };

  const formatPhoneNumber = (phone, contactCode) => {
    const digits = String(phone || "").replace(/\D/g, "");
    const resolvedCountryCode = getCountryCode(contactCode);

    if (digits.length === 10 && resolvedCountryCode === "+1") {
      return `${resolvedCountryCode} (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    if (digits.length === 10 && resolvedCountryCode === "+91") {
      return `${resolvedCountryCode} ${digits.slice(0, 5)}-${digits.slice(5)}`;
    }

    return [resolvedCountryCode, phone].filter(Boolean).join(" ").trim();
  };

  const getDialPhoneNumber = (phone, contactCode) => {
    const digits = String(phone || "").replace(/\D/g, "");
    const countryDigits = getCountryCode(contactCode).replace(/\D/g, "");

    if (!digits) {
      return "";
    }

    return `${countryDigits}${digits}`;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const companyName = company?.name || 'AVPS FlexOffice';
  const phoneNumber = company?.contactPerson?.phoneNumber || "9876543210";
  const countryCode = getCountryCode(company?.contactPerson?.countryCode);
  const fullPhoneNumber = formatPhoneNumber(phoneNumber, countryCode);
  const dialPhoneNumber = getDialPhoneNumber(phoneNumber, countryCode);

  const handleCall = () => {
    window.location.href = `tel:${dialPhoneNumber}`;
  };
  const handleTour = () => {
    window.location.href = "/services";
  };

  const premiumFeatures = [
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Premium Locations",
      description: "Strategically located offices in prime business districts with easy access to transportation hubs and corporate centers.",
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Wifi className="w-8 h-8" />,
      title: "High-Speed Internet",
      description: "Dedicated fiber optic connections with speeds up to 1Gbps, ensuring seamless video conferences and cloud operations.",
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "24/7 Security",
      description: "Advanced security systems with biometric access, CCTV monitoring, and on-site security personnel round the clock.",
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Coffee className="w-8 h-8" />,
      title: "Premium Amenities",
      description: "Complimentary barista-quality coffee, tea station, healthy snacks, and fully equipped kitchenette facilities.",
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Car className="w-8 h-8" />,
      title: "Parking Solutions",
      description: "Dedicated parking spaces, valet services, and EV charging stations for your convenience and sustainability needs.",
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Flexible Capacity",
      description: "Spaces that scale with your business - from single desks to entire floors, accommodating 1-500+ team members.",
      gradient: 'from-blue-500 to-blue-600'
    }
  ];

  const workspaceTypes = [
    {
      title: "Private Offices",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop",
      features: ["Fully furnished", "Customizable layout", "Private entrance", "Dedicated phone line"],
    },
    {
      title: "Shared Workspaces",
      image: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=600&h=400&fit=crop",
      features: ["Hot desking", "Networking opportunities", "Flexible hours", "Community events"],
    },
    {
      title: "Meeting Rooms",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop",
      features: ["Advanced AV equipment", "Video conferencing", "Catering services", "Whiteboard facilities"],
    },
    {
      title: "Virtual Offices",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop",
      features: ["Business address", "Mail handling", "Phone answering", "Meeting room access"],
    }
  ];

  const technicalFeatures = [
    { icon: <Monitor className="w-6 h-6" />, title: "Smart Building Technology", desc: "IoT-enabled infrastructure" },
    { icon: <Printer className="w-6 h-6" />, title: "Business Center", desc: "Printing, scanning, copying" },
    { icon: <Phone className="w-6 h-6" />, title: "Communication Hub", desc: "VoIP systems and conferencing" },
    { icon: <Zap className="w-6 h-6" />, title: "Uninterrupted Power", desc: "UPS backup and generators" },
    { icon: <Headphones className="w-6 h-6" />, title: "Audio-Visual Suite", desc: "Presentation and recording facilities" },
    { icon: <Settings className="w-6 h-6" />, title: "Climate Control", desc: "Personalized environment settings" }
  ];

  const businessBenefits = [
    {
      title: "Cost Efficiency",
      description: "Reduce overhead costs by up to 60% compared to traditional office leasing with all-inclusive pricing.",
      icon: <CreditCard className="w-12 h-12" />,
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: "Instant Setup",
      description: "Move in within 24 to 48 hours with fully furnished, ready-to-use office spaces and IT infrastructure.",
      icon: <Clock className="w-12 h-12" />,
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: "Scalable Solutions",
      description: "Expand or downsize your space requirements instantly without long-term commitments or penalties.",
      icon: <ArrowRight className="w-12 h-12" />,
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: "Professional Image",
      description: "Establish your business presence in prestigious locations with impressive reception and lobby areas.",
      icon: <Star className="w-12 h-12" />,
      gradient: 'from-blue-500 to-blue-600'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <Loader className="animate-spin w-12 h-12 text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <FlexOfficeHeader />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30" style={{ fontFamily: "var(--flex-font, 'Inter', sans-serif)" }}>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16 sm:py-20 lg:py-32">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-500/20 to-transparent rounded-full -translate-y-32 translate-x-32" />

          <div className="relative max-w-7xl mx-auto px-4 text-center">
            <div className="inline-flex items-center bg-gradient-to-r from-blue-100/20 to-slate-100/20 text-blue-300 px-4 py-2 text-sm font-semibold mb-6 border border-blue-300/20 rounded-full">
              <Star className="w-4 h-4 mr-2" />
              Premium Workspace Solutions
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Premium Features &
              <span className="block bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">Amenities</span>
            </h1>
            <p className="text-xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Discover why <span className="font-semibold text-white">{companyName}</span> is the preferred choice for businesses seeking flexible,
              premium office solutions with world-class amenities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleTour}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02]"
              >
                <Calendar className="w-5 h-5 inline mr-2" />
                Book Now
              </button>
              <button
                onClick={handleCall}
                className="border-2 border-slate-300 text-slate-700 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-300 hover:scale-[1.02] flex items-center"
              >
                <Phone className="text-white w-5 h-5 mr-2" />
                 <span className="text-white ml-1">{fullPhoneNumber}</span>
              </button>

            </div>
          </div>
        </section>

        {/* Premium Features Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-gradient-to-r from-blue-100/20 to-slate-100/20 text-blue-600 px-4 py-2 text-sm font-semibold mb-4 border border-blue-200 rounded-full">
                <Building2 className="w-4 h-4 mr-2" />
                Core Features
              </div>
              <h2 className="text-4xl font-bold text-slate-800 mb-4">Everything You Need to Succeed</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Comprehensive amenities designed to enhance productivity and comfort.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {premiumFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="group bg-white backdrop-blur-sm p-8 rounded-2xl border border-slate-200 hover:border-blue-300 transition-all duration-300 hover:-translate-y-1 shadow-lg hover:shadow-xl"
                >
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <div className="text-white">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-4">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Workspace Types */}
        <section className="py-20 bg-slate-50/50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-gradient-to-r from-blue-100/20 to-slate-100/20 text-blue-600 px-4 py-2 text-sm font-semibold mb-4 border border-blue-200 rounded-full">
                <Users className="w-4 h-4 mr-2" />
                Workspace Options
              </div>
              <h2 className="text-4xl font-bold text-slate-800 mb-4">Flexible Solutions for Every Need</h2>
              <p className="text-xl text-slate-600">Choose the perfect workspace that matches your business requirements.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {workspaceTypes.map((workspace, index) => (
                <div key={index} className="group bg-white backdrop-blur-sm rounded-2xl border border-slate-200 overflow-hidden hover:border-blue-300 transition-all duration-300 shadow-lg hover:shadow-xl">
                  <div className="h-48 relative overflow-hidden">
                    <img
                      src={workspace.image}
                      alt={workspace.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-800 mb-4">{workspace.title}</h3>
                    <ul className="space-y-3">
                      {workspace.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-slate-600 text-sm">
                          <CheckCircle className="w-4 h-4 text-blue-600 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Features */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-gradient-to-r from-blue-100/20 to-slate-100/20 text-blue-600 px-4 py-2 text-sm font-semibold mb-4 border border-blue-200 rounded-full">
                <Zap className="w-4 h-4 mr-2" />
                Technology
              </div>
              <h2 className="text-4xl font-bold text-slate-800 mb-4">Advanced Infrastructure</h2>
              <p className="text-xl text-slate-600">State-of-the-art technology to support your business operations.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {technicalFeatures.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-xl border border-slate-200 hover:border-blue-300 transition-all duration-300 shadow-md hover:shadow-lg">
                  <div className="text-blue-600 mt-1 flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 text-sm">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business Benefits */}
        <section className="py-20 bg-slate-50/50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-gradient-to-r from-blue-100/20 to-slate-100/20 text-blue-600 px-4 py-2 text-sm font-semibold mb-4 border border-blue-200 rounded-full">
                <Star className="w-4 h-4 mr-2" />
                Business Advantages
              </div>
              <h2 className="text-4xl font-bold text-slate-800 mb-4">Why Choose {companyName}</h2>
              <p className="text-xl text-slate-600">Experience the benefits of premium flexible office solutions.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {businessBenefits.map((benefit, index) => (
                <div key={index} className="group bg-white backdrop-blur-sm p-8 rounded-2xl border border-slate-200 hover:border-blue-300 transition-all duration-300 shadow-lg hover:shadow-xl">
                  <div className="flex items-start space-x-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${benefit.gradient} rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <div className="text-white">
                        {benefit.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-slate-800 mb-3">{benefit.title}</h3>
                      <p className="text-slate-600 leading-relaxed text-lg">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        {/* <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Join thousands of successful businesses who trust {companyName} for their workspace needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleTour} 
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02]"
              >
                <Calendar className="w-5 h-5 inline mr-2" />
                Book a Tour
              </button>
              <button
                onClick={handleCall}
                className="border-2 border-slate-300 text-slate-700 bg-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-300 hover:scale-[1.02]"
              >
                <Phone className="w-5 h-5 inline mr-2" />
                {fullPhoneNumber}
              </button>
            </div>
            <div className="mt-6">
              <button
                onClick={handleWhatsApp}
                className="inline-flex items-center text-blue-300 hover:text-blue-400 font-medium transition-colors duration-200"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Or message us on WhatsApp
              </button>
            </div>
          </div>
        </section> */}
      </div>
      <FlexOfficeFooter company={company} />
    </>
  );
};

export default FlexFeatures;
